import { useContext, useState, type ReactNode } from 'react';
import {
  HotelResults,
  pendingHotelSearchFields,
  useSoldOutOfferIds,
  type HotelSearchValue,
} from '@voll-intelligence/intelligence-ui-kit';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type {
  ChatUiHotelOffer,
  HotelPlannerBlock,
  HotelPlannerSelection,
  HotelRoomsRateFilters,
} from '@voll-intelligence/genui-contract';
import { draftSelection, type HotelStage } from './hotel_selection.js';
import { dockShareOf, sharedResultsProps } from './hotel_results_dock_props.js';
import { guidedOpenOf, searchProps } from './hotel_search_props.js';
import { HotelResultsLayers } from './hotel_results_dialog.js';
import { fullscreenPortalProps } from './hotel_map_dialog.js';
import { plannerScreenOf, usePlannerPanelList } from './planner_panel_list.js';
import { HotelReadCacheContext } from './hotel_sold_out.js';
import { useHotelSearchOptions } from './use_hotel_search_options.js';
import type { HotelSearchPreviewState } from './use_hotel_search_preview.js';
import { usePanelReport, type PanelReport } from './use_panel_report.js';
import { resultsLocks } from './hotel_results_locks.js';
import { HotelIntroLine, introKeyOf, isNoResults, resultNotice } from './hotel_results_notice.js';
import {
  hotelSearchOptionsInput,
  makeSelectHotel,
  offerHandlers,
  searchTurnDispatcher,
} from './hotel_results_handlers.js';
import {
  useHotelPlannerSelection,
  useHotelResultsDialogs,
  useHotelSearchSubmission,
  usePlannerView,
} from './use_hotel_results_state.js';

/** O card da conversa só busca com um hóspede NOMEADO em cada vaga (#6941): o
 *  agente só reserva assim (`occupancyBookable`, `_book_guests.ts`, que aceita
 *  só o solicitante sem nome), e uma vaga vazia levava a pessoa até a cotação
 *  para ouvir que a reserva não sai. O veto é do host (`canSubmit`): a barra
 *  abre o Quem no lugar da busca. A bancada não monta este organismo e segue
 *  aceitando a vaga vazia. */
function chatSelection(
  value: HotelSearchValue,
  selection: HotelPlannerSelection | null,
  requesterId: string | undefined,
): HotelPlannerSelection | null {
  if (!selection || pendingHotelSearchFields(value).includes('rooms')) return null;
  const named = (selection.travelers ?? []).every(
    ({ id, name }) => id === requesterId || name.trim() !== '',
  );
  return named ? selection : null;
}

type HotelResultsOrganismCommonProps = {
  ui: HotelPlannerBlock;
  stage: HotelStage;
  readOnly?: boolean;
  /** A conversa andou depois deste card: refinar deixa de ser leitura in-place e
   *  passa a ser TURNO (mensagem nova → planner novo). */
  superseded?: boolean;
  busy?: boolean;
  /** Bloco remontado do histórico (`GenUiRenderProps.replay`): o guia da
   *  barra fica, mas o primeiro campo não nasce aberto. */
  replay?: boolean;
  /** Conversational sink. Ausente, escolher um hotel não faz nada. A bancada
   *  não monta este organismo: monta `HotelDeterministicSearch` e
   *  `HotelDeterministicResultsPage`, sem Context. */
  dispatch?: GenUiDispatch;
  previewState: HotelSearchPreviewState;
  onSearch: (selection: HotelPlannerSelection) => Promise<void>;
  onRetry: (selection?: HotelPlannerSelection) => Promise<void>;
  onOpenDetails: (offer: ChatUiHotelOffer, rateFilters: HotelRoomsRateFilters) => void;
  /** A gaveta de detalhes do host, quando a lista ou o mapa cheio estão
   *  abertos: monta DENTRO da tela em diálogo do topo (o mapa, senão a lista),
   *  como diálogo aninhado — nunca irmão dela. */
  detailsLayer?: ReactNode;
  onExpandedRemount?: () => void;
  /** `false` → nenhuma busca dispara sozinha; só o submit do form. Default `true`. */
  autoSearch?: boolean;
  /** M1 · 1.1b — o painel/total que o CARD precisa para alimentar
   *  `useHotelSearchContextSync({ screen })`. Chamado a cada MUDANÇA de
   *  conteúdo (não a cada render); ausente no caminho determinístico, que
   *  não sincroniza contexto de refino. */
  onPanelReport?: (report: PanelReport) => void;
};

// `dockOfferLimit` corta o DOCK e os pinos do mapa compacto no mesmo número
// (3 cards ⇒ 3 pinos), PRESERVANDO a lista completa como semente do portal "Ver
// todos os hotéis" — o portal segue com todas as ofertas, filtros e ordenação.
// O corte mora aqui, e não num slice do host sobre `ui.offers`, porque cortar na
// entrada rebaixaria a lista completa do portal para o mesmo teto. A página
// determinística da bancada não é deste organismo: é a
// `HotelDeterministicResultsPage`, que o host monta sem Context.
export type HotelResultsOrganismProps = HotelResultsOrganismCommonProps & {
  dockOfferLimit?: number;
};

export function HotelResultsOrganism({
  ui,
  stage,
  readOnly = false,
  superseded = false,
  busy = false,
  replay = false,
  dispatch,
  previewState,
  onSearch,
  onRetry,
  onOpenDetails,
  detailsLayer,
  onExpandedRemount,
  autoSearch = true,
  onPanelReport,
  dockOfferLimit,
}: Readonly<HotelResultsOrganismProps>): ReactNode {
  const config = useGenUiConfig();
  const { locale, t } = useGenUiI18n();
  const sourceOffers = ui.offers ?? [];
  const view = usePlannerView({ ui, config, locale });
  const [searchValue, setSearchValue] = useState(view.searchValue);
  const options = useHotelSearchOptions(
    hotelSearchOptionsInput(ui, searchValue, locale, config.ports),
  );
  const dialogs = useHotelResultsDialogs(onExpandedRemount, ui.directives?.open);
  const locks = resultsLocks({
    ui,
    stage,
    readOnly,
    busy,
    superseded,
    dispatch,
    fullscreen: false,
  });
  const surfaces = { config, ui, view, surfacesOpen: dialogs.surfacesOpen };
  const soldOut = useSoldOutOfferIds(useContext(HotelReadCacheContext));
  const list = usePlannerPanelList({ ...surfaces, locale, soldOut });
  // M1 · 1.1b/Onda 2: o painel reportado ao card — helper fora daqui (teto de linhas).
  const screen = plannerScreenOf(list, surfaces.surfacesOpen, sourceOffers, ui);
  usePanelReport(screen, onPanelReport);
  const selection = chatSelection(
    searchValue,
    useHotelPlannerSelection(ui, options, searchValue),
    ui.requesterId,
  );

  const searchSubmission = useHotelSearchSubmission({
    ui,
    selection,
    locked: locks.searchLocked,
    superseded: locks.searchIsTurn,
    autoSearch,
    expired: list.page.phase === 'stale',
    onSearch,
    onRetry,
    onTurnSearch: searchTurnDispatcher({ ui, searchValue, locale, dispatch, t }),
    selectionOf: (value) =>
      chatSelection(value, draftSelection(ui, options.resolveValue(value)), ui.requesterId),
  });
  const { openDetails } = offerHandlers({ list, sourceOffers, onOpenDetails });
  const selectHotel = makeSelectHotel({
    sourceOffers,
    locked: locks.locked,
    dispatch,
    ui,
    previewState,
    screen,
    t,
  });
  const search = searchProps({
    value: searchValue,
    copy: view.searchCopy,
    locale,
    requesterId: ui.requesterId,
    options,
    locks,
    busy,
    guidedOpen: guidedOpenOf(replay, dispatch),
    canSubmit: searchSubmission.canSubmit,
    onChange: setSearchValue,
    onSubmit: searchSubmission.submit,
  });
  const notice = resultNotice(ui, stage, previewState, t, view.offers.length);
  const introKey = introKeyOf(searchValue, notice.state, isNoResults(notice, stage));
  const sharedProps = sharedResultsProps({
    // `view.offers`, JÁ FILTRADO por `directives` (M1 · 1.2) — não `ui.offers`
    // cru: ver a doc de `resultNotice`.
    notice,
    selection,
    view,
    stage,
    ui,
    search,
    locked: locks.locked,
    busy,
    dockOfferLimit,
    share: dockShareOf({ ports: config.ports, ui, t, locks }),
    onRetry: searchSubmission.retry,
    onHotelSelect: selectHotel,
    onOpenDetails: openDetails,
  });

  return (
    <div
      className="genui2 grid w-full gap-4"
      aria-busy={busy || previewState.phase === 'loading' || undefined}
    >
      {dispatch && <HotelIntroLine introKey={introKey} />}
      <HotelResults
        {...sharedProps}
        onMapExpand={dialogs.fullscreenMap.open}
        onSeeAll={dialogs.allHotels.open}
      />
      <HotelResultsLayers
        dialogs={dialogs}
        view={view}
        list={list}
        ui={ui}
        search={search}
        locks={locks}
        expiredRetry={searchSubmission.expiredRetry}
        // O MESMO cadeado do painel da lista nos dois (cortes/ordem/cards do
        // mapa), não o da escolha: na bancada (`fullscreen`), `readOnly` não
        // trava a leitura.
        mapProps={fullscreenPortalProps(dispatch, searchSubmission.expiredRetry)}
        onOpenDetails={openDetails}
        detailsLayer={detailsLayer}
      />
    </div>
  );
}
